"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, RefreshCw, ExternalLink } from "lucide-react";
import {
  usePersonaStore,
  usePersonaHasHydrated,
  usePhase1Personas,
  PERSONA_AVATAR_COLORS,
  CUSTOMER_TYPE_KEYS,
} from "@/lib/persona";
import { useDemoToolbarStore } from "@/lib/demo-toolbar-store";
import { resetDemoSession } from "@/lib/demo-reset";
import { useT } from "@/lib/locale";
import { useRouter, usePathname } from "next/navigation";
import { shouldRedirectOnPersonaSwitch } from "@/lib/persona-routing";
import { usePayFlowStore } from "@/lib/pay-flow";
import { useDismissable } from "@petci/shared-ui";
import type { PersonaId } from "@petci/fixtures";

const SHOULD_ENTER_APP = ["/sign-in", "/accessibility"] as const;

export default function DemoToolbar({ className }: { className?: string }) {
  const t = useT();
  const router = useRouter();
  const pathname = usePathname();
  const personas = usePhase1Personas();
  const activeId = usePersonaStore((s) => s.activeId);
  const setPersona = usePersonaStore((s) => s.setPersona);
  const hasHydrated = usePersonaHasHydrated();
  const open = useDemoToolbarStore((s) => s.open);
  const setOpen = useDemoToolbarStore((s) => s.setOpen);
  const toggle = useDemoToolbarStore((s) => s.toggle);
  const [resetFlash, setResetFlash] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    };
  }, []);

  const closeToolbar = useCallback(() => setOpen(false), [setOpen]);
  useDismissable({ open, onClose: closeToolbar, ref: containerRef });

  const payFlowActive = usePayFlowStore((s) => s.active);
  const [showLockHint, setShowLockHint] = useState(false);

  function handlePersonaClick(id: PersonaId) {
    if (payFlowActive) {
      setShowLockHint(true);
      setTimeout(() => setShowLockHint(false), 2000);
      return;
    }
    setPersona(id);
    setOpen(false);
    if (pathname === "/") return;
    if (SHOULD_ENTER_APP.some((p) => pathname.startsWith(p))) {
      router.push("/home");
      return;
    }
    const redirect = shouldRedirectOnPersonaSwitch(id, pathname);
    if (redirect) router.push(redirect);
  }

  function handleReset() {
    resetDemoSession();
    setResetFlash(true);
    if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = setTimeout(() => setResetFlash(false), 1200);
  }

  return (
    <div
      ref={containerRef}
      className={[
        "relative inline-flex items-center",
        className ?? "",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls="demo-toolbar-popover"
        aria-label={`${t("toolbar.label")} — ${open ? t("toolbar.collapse") : t("toolbar.expand")}`}
        /* F-5: paint and hit area are separated here. The pill's own box is
           19px tall — under the 24px floor — but it is PAINTED, so padding it
           to 24 would make it ~40% taller on every authenticated route. Instead
           the button is a transparent 27px target (`py-1` grows the box,
           `-my-1` gives the space back so nothing shifts) and the span below
           keeps the exact pill it always was. `getBoundingClientRect`, which is
           what audit:e6 measures, now reads the real target. A pseudo-element
           would have fixed the usability while leaving the audit red. */
        className="group inline-flex items-center py-1 -my-1"
      >
        <span className="inline-flex items-center gap-1 bg-brand-accent text-neutral-900 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full group-hover:brightness-95 transition-all">
          {t("toolbar.label")}
          <ChevronDown
            size={10}
            aria-hidden
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {open && (
        <div
          id="demo-toolbar-popover"
          role="menu"
          aria-label={t("toolbar.label")}
          className="absolute left-0 top-full mt-2 w-72 bg-white border border-neutral-200 rounded-lg shadow-lg p-3 z-50 animate-fade-in"
        >
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-2">
              {t("toolbar.activePersona")}
            </p>
            <ul
              className="grid grid-cols-4 gap-1.5"
              role="group"
              aria-label={t("toolbar.activePersona")}
            >
              {personas.map((p) => {
                const isActive = hasHydrated && p.id === activeId;
                return (
                  <li key={p.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handlePersonaClick(p.id)}
                      aria-current={isActive ? "true" : undefined}
                      aria-label={`${p.contactName} — ${t(CUSTOMER_TYPE_KEYS[p.customerType] ?? "toolbar.personaTypeResidential")}`}
                      className={[
                        "w-full flex flex-col items-center gap-1 p-1.5 rounded-md transition-all",
                        isActive
                          ? "bg-brand-primary/10 ring-1 ring-brand-primary"
                          : "hover:bg-neutral-50",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold",
                          PERSONA_AVATAR_COLORS[p.id] ?? "bg-neutral-300",
                        ].join(" ")}
                        aria-hidden
                      >
                        {p.contactName.charAt(0)}
                      </span>
                      <span className="text-[10px] font-medium text-neutral-700 truncate w-full text-center">
                        {p.contactName.split(" ")[0]}
                      </span>
                      <span className="text-[9px] text-neutral-500 truncate w-full text-center">
                        {t(CUSTOMER_TYPE_KEYS[p.customerType] ?? "toolbar.personaTypeResidential")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="space-y-1.5 pt-2 border-t border-neutral-100">
            <button
              type="button"
              role="menuitem"
              onClick={handleReset}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50 rounded-md transition-colors"
            >
              <RefreshCw size={12} aria-hidden />
              {t("toolbar.reset")}
            </button>
            {resetFlash && (
              <span
                className="block text-[10px] text-semantic-success-text truncate px-2"
                role="status"
                aria-live="polite"
              >
                {t("toolbar.resetConfirmation")}
              </span>
            )}
            <a
              href="https://pelicanenergytci.com"
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-brand-primary-text hover:bg-neutral-50 rounded-md transition-colors"
            >
              <ExternalLink size={12} aria-hidden />
              {t("toolbar.exit")}
            </a>
          </div>
        </div>
      )}
      {showLockHint && (
        <div className="absolute top-full mt-1 left-0 bg-neutral-800 text-white text-xs rounded-lg px-3 py-1.5 whitespace-nowrap shadow-lg z-50">
          {t("toolbar.payLockHint")}
        </div>
      )}
    </div>
  );
}
